import React, { useState } from "react";
import { Grid, Collapse } from "@material-ui/core";
import { CustomTextField } from "./SellAiStyledComponents";

interface WizardStep6PriceProps {
  aiData: any;
  sellOption: 'consign' | 'live' | 'valuation' | 'none_of_the_above' | "";
  setSellOption: (option: 'consign' | 'live' | 'valuation' | 'none_of_the_above' | "") => void;
  askingPrice: string;
  setAskingPrice: (val: string) => void;
}

const WizardStep6Price: React.FC<WizardStep6PriceProps> = ({ aiData, sellOption, setSellOption, askingPrice, setAskingPrice }) => {
  const [showHow, setShowHow] = useState(false);

  const currentDate = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).toUpperCase();

  const estimatedPrice = aiData?.estimatedMarketPriceUSD || 0;
  const lowEstimate = aiData?.minPrice || 0;
  const highEstimate = aiData?.maxPrice || 0;

  const formatPrice = (price: number) => price.toLocaleString('en-US', { maximumFractionDigits: 0 });
  const formatCompact = (price: number) => (price / 1000).toFixed(1) + 'k';

  const confidence = aiData?.confidence?.toUpperCase() || '';

  const meltValue = aiData?.meltValueUSD || 0;
  const displayWeight = aiData?.metalWeight ?? aiData?.weight ?? '0';
  const metal = aiData?.metal || '';
  const metalPurityPercentage = aiData?.metalPurityPercentage ? `${aiData.metalPurityPercentage}%` : '';

  const comparableSalesCount = aiData?.comparableSalesCount?.toLocaleString('en-US') || '0';
  const dataFromYear = aiData?.dataFromYear;
  const dataToYear = aiData?.dataToYear;

  const evidence = 'Certified';
  const evidenceDetails = 'carat, clarity and colour taken from your certificate';

  const minPrice = estimatedPrice * 0.7;
  const maxPrice = estimatedPrice * 1.3;
  const getX = (price: number) => {
    const ratio = (price - minPrice) / (maxPrice - minPrice);
    const clampedRatio = Math.max(0.1, Math.min(ratio, 0.9));
    return clampedRatio * 400;
  };
  const line1X = getX(lowEstimate);
  const line2X = getX(highEstimate);

  const getBadgeStyle = (
  type: 'photo' | 'hallmark' | 'certificate' | 'verified' | 'user' | 'optional'
) => {
  const isPhoto = type === 'photo';
  const isUser = type === 'user';
  const isOptional = type === 'optional';

  return {
    fontSize: '0.6rem',
    fontWeight: 700,
    padding: '0.15rem 0.5rem',
    borderRadius: '12px',
    background: 'transparent',
    color: isPhoto || isUser
      ? '#d92b3c'
      : isOptional
        ? '#4d9b78'
        : '#999',
    border: `1px solid ${
      isPhoto || isUser
        ? '#ffb3c6'
        : isOptional
          ? '#a8d5c0'
          : '#EAEAEA'
    }`,
    letterSpacing: '0.5px',
    textTransform: 'uppercase' as const,
  };
};

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <div style={{ background: '#fff', color: '#1A1A1A', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>

          <div style={{ color: '#d92b3c', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '2px', marginBottom: '1rem', textTransform: 'uppercase' }}>ESTIMATED CLEARING RANGE</div>
          <div style={{ fontSize: 'clamp(1.1rem, 5vw, 3.5rem)', fontWeight: 'bold', fontFamily: '"Playfair Display", serif', marginBottom: '0.5rem', letterSpacing: '-1px', whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: 'clamp(0.8rem, 3vw, 1.5rem)', verticalAlign: 'super', marginRight: '0.5rem', color: '#666', letterSpacing: '0', fontFamily: 'system-ui, -apple-system, sans-serif' }}>USD</span>
            {formatPrice(lowEstimate)} – {formatPrice(highEstimate)}
          </div>
          <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '2rem' }}>where a piece like yours has actually cleared at Zipang — not a retail, list or insurance figure</div>

          <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1.5px', color: '#999', marginBottom: '0.5rem' }}>
            CONFIDENCE <span style={{ color: '#d92b3c', letterSpacing: '-2px', margin: '0 0.5rem', fontWeight: 900 }}>―――</span> <span style={{ color: '#d92b3c' }}>{confidence}</span>
          </div>
          <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', color: '#999', marginBottom: '3rem' }}>
            PRICED BY CARAT PRO ENGINE V1 &middot; COMPUTED {currentDate}
          </div>

          <div style={{ height: '140px', position: 'relative', maxWidth: '600px', margin: '0 auto 3rem auto' }}>
            <svg viewBox="0 0 400 140" style={{ width: '100%', height: '100%' }} preserveAspectRatio="none">
              <path d="M0 130 C 100 130, 150 20, 200 20 C 250 20, 300 130, 400 130" fill="url(#curveGradient)" stroke="#d92b3c" strokeWidth="1.5" />
              <defs>
                <linearGradient id="curveGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#d92b3c" stopOpacity="0.15" />
                  <stop offset="100%" stopColor="#fff" stopOpacity="0" />
                </linearGradient>
              </defs>
              <line x1={line1X} y1="30" x2={line1X} y2="130" stroke="#d92b3c" strokeWidth="1" strokeDasharray="3 3" opacity="0.5" />
              <line x1={line2X} y1="30" x2={line2X} y2="130" stroke="#d92b3c" strokeWidth="1" strokeDasharray="3 3" opacity="0.5" />
              <text x={line1X} y="20" fill="#999" fontSize="10" textAnchor="middle" fontWeight="bold">{formatCompact(lowEstimate)}</text>
              <text x={line2X} y="20" fill="#999" fontSize="10" textAnchor="middle" fontWeight="bold">{formatCompact(highEstimate)}</text>
            </svg>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginBottom: '3rem' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '1px', color: '#999', textTransform: 'uppercase' }}>
              YOUR ASKING PRICE <span style={getBadgeStyle('optional')}>
  OPTIONAL
</span>
            </div>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <span style={{ position: 'absolute', left: '1.25rem', color: '#1A1A1A', fontWeight: 'bold', fontSize: '0.9rem', zIndex: 1, pointerEvents: 'none' }}>$</span>
              <input
                type="text"
                pattern="[0-9]*"
                placeholder="e.g. 11,000"
                value={askingPrice}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  setAskingPrice(e.target.value.replace(/[^\d]/g, ''));
                }}
                style={{
                  border: '1px solid #d92b3c',
                  borderRadius: '24px',
                  padding: '0.6rem 1.25rem 0.6rem 2rem',
                  fontSize: '0.9rem',
                  color: '#1A1A1A',
                  outline: 'none',
                  width: '140px',
                  textAlign: 'center',
                  fontWeight: 'bold',
                  fontFamily: 'inherit',
                  background: '#fff',
                  boxShadow: askingPrice && parseInt(askingPrice, 10) <= 0 ? '0 0 0 2px #d92b3c20' : 'none',
                }}
              />
            </div>
          </div>
          {askingPrice && parseInt(askingPrice, 10) <= 0 && (
            <div style={{ color: '#d92b3c', fontSize: '0.8rem', textAlign: 'center', marginTop: '-2.5rem', marginBottom: '2rem', fontWeight: 600 }}>
              Asking price must be a natural number greater than 0.
            </div>
          )}


          <Grid container spacing={2} style={{ textAlign: 'left', marginBottom: '2rem' }}>
            {aiData?.meltValueUSD !== null && (
              <Grid item xs={12} sm={4}>
                <div style={{ background: '#FAFAFA', border: '1px solid #EAEAEA', borderRadius: '8px', padding: '1.25rem', height: '100%' }}>
                  <div style={{ color: '#999', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', marginBottom: '0.75rem', textTransform: 'uppercase' }}>MELT FLOOR</div>
                  <div style={{ fontSize: '1.1rem', color: '#666', marginBottom: '0.5rem' }}>${formatPrice(meltValue)}</div>
                  <div style={{ color: '#1A1A1A', fontSize: '0.75rem', lineHeight: 1.4 }}>{String(displayWeight).replace(/[a-zA-Z\s]+$/g, '').trim()}g of {metal} at {metalPurityPercentage} &middot; spot {currentDate}</div>
                </div>
              </Grid>
            )}
            <Grid item xs={12} sm={aiData?.meltValueUSD !== null ? 4 : 6}>
              <div style={{ background: '#FAFAFA', border: '1px solid #EAEAEA', borderRadius: '8px', padding: '1.25rem', height: '100%' }}>
                <div style={{ color: '#999', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', marginBottom: '0.75rem', textTransform: 'uppercase' }}>PAST CLEARED SALES</div>
                <div style={{ fontSize: '1.1rem', color: '#666', marginBottom: '0.5rem' }}>
                  ${formatPrice(lowEstimate)}–{formatPrice(highEstimate)}
                </div>
                <div style={{ color: '#1A1A1A', fontSize: '0.75rem', lineHeight: 1.4 }}><span style={{ color: '#d92b3c' }}>{comparableSalesCount}</span> comparable sales {dataFromYear}-{dataToYear} - weighted to recent</div>
              </div>
            </Grid>
            <Grid item xs={12} sm={aiData?.meltValueUSD !== null ? 4 : 6}>
              <div style={{ background: '#FAFAFA', border: '1px solid #EAEAEA', borderRadius: '8px', padding: '1.25rem', height: '100%' }}>
                <div style={{ color: '#999', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', marginBottom: '0.75rem', textTransform: 'uppercase' }}>EVIDENCE</div>
                <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#d92b3c' }}>{evidence}</span> — {evidenceDetails}
                </div>
              </div>
            </Grid>
          </Grid>

          <div
            onClick={() => setShowHow(!showHow)}
            style={{ border: '1px solid #FCEBEA', background: '#FFF0F2', padding: '1rem', borderRadius: '8px', color: '#d92b3c', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '1px', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}
          >
            HOW THIS NUMBER IS MADE <span style={{ fontSize: '1.2rem', lineHeight: 0.5, fontWeight: 'normal' }}>{showHow ? '−' : '+'}</span>
          </div>

          <Collapse in={showHow}>
            <div style={{ background: '#FAFAFA', border: '1px solid #EAEAEA', borderRadius: '8px', padding: '1.5rem', color: '#666', fontSize: '0.8rem', lineHeight: 1.6, textAlign: 'left', marginBottom: '1rem' }}>
              <p style={{ margin: '0 0 1rem 0' }}>We match your piece against diamond jewelry that has actually cleared at Zipang auctions — real hammer prices, weighted toward recent results. It is not a list price, a retail price, or an appraisal figure.</p>
              <p style={{ margin: '0 0 1rem 0' }}>The range widens when we have fewer close comparables, or when a detail was read from a photo rather than a certificate. A tighter range means we've seen more pieces like yours.</p>
              <p style={{ margin: '0 0 1rem 0' }}>The melt floor is the metal on its own — stone weight removed, purity applied, at today's spot. It's a floor, not a component: a piece almost never clears below it, and a good one clears far above.</p>
              <p style={{ margin: 0 }}>Carat Pro is the pricing engine behind this number, built on the My Auctions group's cleared-sale record. From September you'll be able to check a piece with it directly — whether or not you sell with us.</p>
            </div>
          </Collapse>

          <div style={{ background: '#FFF0F2', borderRadius: '8px', padding: '1.5rem', color: '#1A1A1A', fontSize: '0.85rem', lineHeight: 1.6, textAlign: 'left', marginBottom: '4rem' }}>
            This is an instant estimate. <span style={{ fontWeight: 'bold' }}>Consign the piece and our gemologist confirms a firm offer — free, always.</span> The gap between estimate and firm price is closed on inspection, and talking to us costs nothing no matter how many valuations you've run.
          </div>
        </div>

        <div style={{ textAlign: 'left' }}>
          <div style={{ fontSize: '1.25rem', fontFamily: '"Playfair Display", serif', fontWeight: 'bold', color: '#1A1A1A', marginBottom: '0.25rem' }}>Choose how to sell</div>
          <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1.5rem' }}>
            Based on your estimate, we'd route this to a <span style={{ fontWeight: 'bold', color: '#d92b3c' }}>live auction</span> — but it's your call.
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
            {/* Option 1 */}
            <div onClick={() =>
  setSellOption(sellOption === "consign" ? "" : "consign")
} style={{ border: sellOption === 'consign' ? '1.5px solid #d92b3c' : '1px solid #EAEAEA', borderRadius: '8px', padding: '1.5rem', background: sellOption === 'consign' ? '#FFF0F2' : '#fff', position: 'relative', cursor: 'pointer', display: 'flex', gap: '1rem', alignItems: 'flex-start', transition: 'all 0.2s ease' }}>
              <div style={{ position: 'absolute', top: '-10px', right: '1.5rem', background: '#d92b3c', color: '#fff', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '1px', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>RECOMMENDED</div>
              <div style={{ width: '32px', height: '32px', background: sellOption === 'consign' ? '#d92b3c' : '#FFF0F2', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: sellOption === 'consign' ? '#fff' : '#d92b3c', transition: 'all 0.2s ease', marginTop: '0.25rem' }}>
                <svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2L2 12l10 10 10-10L12 2z" /></svg>
              </div>
              <div>
                <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#1A1A1A', marginBottom: '0.25rem' }}>Consign to a Zipang auction</div>
                <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.75rem', lineHeight: 1.5 }}>Best for higher-value pieces. Global buyers compete for it, you keep more of the hammer, and you're paid fastest.</div>
                <div style={{ fontSize: '0.75rem', color: '#d92b3c', fontWeight: 600, letterSpacing: '0.5px' }}>3% commission &middot; fastest to cash</div>
              </div>
            </div>

            {/* Option 2 */}
            <div onClick={() => setSellOption(sellOption === 'live' ? "" : "live")} style={{ border: sellOption === 'live' ? '1.5px solid #d92b3c' : '1px solid #EAEAEA', borderRadius: '8px', padding: '1.5rem', background: sellOption === 'live' ? '#FFF0F2' : '#fff', position: 'relative', cursor: 'pointer', display: 'flex', gap: '1rem', alignItems: 'flex-start', transition: 'all 0.2s ease', flexDirection: 'column' }}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', width: '100%' }}>
                <div style={{ width: '32px', height: '32px', background: sellOption === 'live' ? '#d92b3c' : '#FFF0F2', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: sellOption === 'live' ? '#fff' : '#d92b3c', transition: 'all 0.2s ease', marginTop: '0.25rem' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                </div>
                <div>
                  <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#1A1A1A', marginBottom: '0.25rem' }}>List on Jewel Live</div>
                  <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.75rem', lineHeight: 1.5 }}>Sell into a live-stream audience of retail buyers.</div>
                  <div style={{ fontSize: '0.75rem', color: '#d92b3c', fontWeight: 600, letterSpacing: '0.5px' }}>5% commission &middot; settles ~1 month</div>
                </div>
              </div>
            </div>

            {/* Option 3 */}
            <div onClick={() => setSellOption(sellOption === "valuation" ? "" : "valuation")} style={{ border: sellOption === 'valuation' ? '1.5px solid #d92b3c' : '1px solid #EAEAEA', borderRadius: '8px', padding: '1.5rem', background: sellOption === 'valuation' ? '#FFF0F2' : '#fff', position: 'relative', cursor: 'pointer', display: 'flex', gap: '1rem', alignItems: 'flex-start', transition: 'all 0.2s ease' }}>
              <div style={{ width: '32px', height: '32px', background: sellOption === 'valuation' ? '#d92b3c' : '#FFF0F2', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: sellOption === 'valuation' ? '#fff' : '#d92b3c', transition: 'all 0.2s ease', marginTop: '0.25rem' }}>
                <svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" fill="currentColor" viewBox="0 0 24 24"><path d="M4 4h16v2H4zm0 6h16v2H4zm0 6h16v2H4z" /></svg>
              </div>
              <div>
                <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#1A1A1A', marginBottom: '0.25rem' }}>Get a certified valuation</div>
                <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.75rem', lineHeight: 1.5 }}>A documented valuation you keep — for insurance, estate or records. In-hand gemologist inspection. Keep the piece; sell whenever you like.</div>
                <div style={{ fontSize: '0.75rem', color: '#d92b3c', fontWeight: 600, letterSpacing: '0.5px' }}>paid service &middot; document in 1 business day</div>
              </div>
            </div>

            {/* Option 4 */}
            <div onClick={() => setSellOption(sellOption === "none_of_the_above" ? "" : "none_of_the_above")} style={{ border: sellOption === 'none_of_the_above' ? '1.5px solid #d92b3c' : '1px solid #EAEAEA', borderRadius: '8px', padding: '1.5rem', background: sellOption === 'none_of_the_above' ? '#FFF0F2' : '#fff', position: 'relative', cursor: 'pointer', display: 'flex', gap: '1rem', alignItems: 'flex-start', transition: 'all 0.2s ease' }}>
              <div style={{ width: '32px', height: '32px', background: sellOption === 'none_of_the_above' ? '#d92b3c' : '#FFF0F2', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: sellOption === 'none_of_the_above' ? '#fff' : '#d92b3c', transition: 'all 0.2s ease', marginTop: '0.25rem' }}>
                <svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" fill="currentColor" viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              </div>
              <div>
                <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#1A1A1A', marginBottom: '0.25rem' }}>None of the above</div>
                <div style={{ fontSize: '0.85rem', color: '#666', lineHeight: 1.5 }}>Not ready to choose a route yet. We'll save your estimate and you can decide later.</div>
              </div>
            </div>
          </div>
        </div>

      </Grid>
    </Grid>
  );
};

export default WizardStep6Price;
